import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Warning } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// ExpiryBanner
// Sticky top banner shown to a tenant when their plan expires within 3 days.
// Behaviour:
//   • Hidden if no tenant or no expiry data.
//   • Hidden if tenant is global_super_admin.
//   • >3d away         → not shown.
//   • ≤3d and >1d away → shown, dismissible (close hides it for current session).
//   • ≤1d away         → shown, NOT dismissible, flashing styling.
//   • Real-time HH:MM:SS countdown, re-rendered every second.
// ─────────────────────────────────────────────────────────────────────────────

const DISMISS_KEY_PREFIX = 'musicsync_expiry_dismiss_';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const THREE_DAYS_MS = 3 * ONE_DAY_MS;

const formatRemaining = (ms) => {
  if (ms <= 0) return '0d 0h 0m 0s';
  const d = Math.floor(ms / ONE_DAY_MS);
  const h = Math.floor((ms % ONE_DAY_MS) / (60 * 60 * 1000));
  const m = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const s = Math.floor((ms % (60 * 1000)) / 1000);
  return `${d}d ${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
};

const ExpiryBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);

  // Tick every second for the countdown
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Restore "dismissed" state per tenant + per expires_at so a new cycle resets
  useEffect(() => {
    if (!user?.tenant_id || !user?.tenant_expires_at) return;
    const key = `${DISMISS_KEY_PREFIX}${user.tenant_id}_${user.tenant_expires_at}`;
    try { setDismissed(!!sessionStorage.getItem(key)); } catch (e) { /* ignore */ }
  }, [user?.tenant_id, user?.tenant_expires_at]);

  // Guards — nothing to show
  if (!user) return null;
  if (user.role === 'global_super_admin') return null;
  if (!user.tenant_expires_at) return null;

  const expiresMs = new Date(user.tenant_expires_at).getTime();
  if (isNaN(expiresMs)) return null;

  const remaining = expiresMs - now;
  if (remaining > THREE_DAYS_MS) return null;     // too far out — no banner
  if (remaining <= 0) return null;                // already expired — /expired route handles this

  const critical = remaining <= ONE_DAY_MS;
  const canDismiss = !critical && !dismissed;

  if (!critical && dismissed) return null;

  const handleDismiss = () => {
    try {
      const key = `${DISMISS_KEY_PREFIX}${user.tenant_id}_${user.tenant_expires_at}`;
      sessionStorage.setItem(key, '1');
    } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  const bgClass = critical
    ? 'bg-gradient-to-r from-red-600 via-red-500 to-red-600 animate-pulse'
    : 'bg-gradient-to-r from-amber-500 to-orange-500';

  return (
    <div
      className={`${bgClass} text-white text-sm font-mono px-4 py-2 flex items-center justify-center gap-4 shadow-lg sticky top-0 z-40`}
      data-testid="expiry-banner"
    >
      <Warning size={18} weight="fill" />
      <span className="font-semibold" data-testid="expiry-banner-message">
        {critical ? 'Your plan expires in ' : 'Your plan expires in '}
        <span data-testid="expiry-countdown">{formatRemaining(remaining)}</span>
      </span>
      <button
        onClick={() => navigate('/dashboard/subscription')}
        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full font-semibold tracking-wide transition-colors"
        data-testid="expiry-banner-renew-btn"
      >
        Renew now ▶
      </button>
      {canDismiss && (
        <button
          onClick={handleDismiss}
          className="opacity-80 hover:opacity-100 transition-opacity"
          data-testid="expiry-banner-dismiss"
          aria-label="Dismiss"
        >
          <X size={16} weight="bold" />
        </button>
      )}
    </div>
  );
};

export default ExpiryBanner;
